import { Alert, Anchor, Button, Divider, Group, Stack, Text, TextInput } from '@mantine/core';
import { Cloud, ExternalLink, RefreshCw, ShieldCheck } from 'lucide-react';
import type {
    InstallFlowDescriptor,
    LaunchActionDescriptor,
    ProviderAuthMethod,
    ProviderConfigField,
    ProviderType,
} from '@bmai/shared';
import type { ProviderOption } from '../IntegrationsService';
import { renderField } from './renderField';
import ConnectionTenantSecret from './ConnectionTenantSecret';
import { useDefaultValueTemplates } from './useDefaultValueTemplates';

/**
 * Resolves a launch-action URL on the client when the host has enough context
 * to build it directly (e.g. AWS Bedrock quick-create URL built from the BFF
 * runtime-config + the current form values). Return `null` to fall back to
 * the server callback route (`onCallProviderRoute`). The wizard renders the
 * launch button as disabled when this returns `null` AND no server route is
 * configured — but in practice every launchActions entry has a server route,
 * so the fallback path always exists.
 */
export type LaunchUrlResolver = (
    providerTypeId: string,
    route: string,
    config: Record<string, unknown>,
) => string | null;

interface Props {
    providerType: ProviderType;
    method: ProviderAuthMethod;
    fields: ProviderConfigField[];
    launchActions: LaunchActionDescriptor[];
    config: Record<string, unknown>;
    editing: boolean;
    saving: boolean;
    installFlow?: InstallFlowDescriptor;
    installFallbackMode: boolean;
    installFallbackValue: string;
    onFieldChange: (name: string, value: unknown) => void;
    onLoadOptions: (route: string) => Promise<ProviderOption[]>;
    /** Fetches a server-minted tenant-secret value via the provider callback route. */
    onFetchTenantSecret: (providerTypeId: string, route: string) => Promise<{ value: string }>;
    /** Calls a provider callback with the current config; returns whatever the route returns (used for launchActions). */
    onCallProviderRoute: (providerTypeId: string, route: string, body: Record<string, unknown>) => Promise<unknown>;
    /** Optional client-side resolver for launch-action URLs. See LaunchUrlResolver. */
    onResolveLaunchUrl?: LaunchUrlResolver;
    onStartOauth: () => void;
    onStartInstall: () => void;
    onSubmitInstallFallback: () => void;
    onInstallFallbackValueChange: (value: string) => void;
    onSwitchToInstallFallback: () => void;
    onBackFromInstallFallback: () => void;
    onReauthenticate?: () => void;
}

export default function ConnectionStepAuth(props: Props): React.ReactElement {
    const {
        providerType,
        method,
        fields,
        launchActions,
        config,
        editing,
        saving,
        installFlow,
        installFallbackMode,
        installFallbackValue,
        onFieldChange,
        onLoadOptions,
        onFetchTenantSecret,
        onCallProviderRoute,
        onResolveLaunchUrl,
        onStartOauth,
        onStartInstall,
        onSubmitInstallFallback,
        onInstallFallbackValueChange,
        onSwitchToInstallFallback,
        onBackFromInstallFallback,
        onReauthenticate,
    } = props;

    const displayConfig = useDefaultValueTemplates(fields, config, onFieldChange);

    if (installFlow && !editing) {
        return (
            <InstallFlowAuth
                installFlow={installFlow}
                saving={saving}
                fallbackMode={installFallbackMode}
                fallbackValue={installFallbackValue}
                onStartInstall={onStartInstall}
                onSwitchToFallback={onSwitchToInstallFallback}
                onBackFromFallback={onBackFromInstallFallback}
                onSubmitFallback={onSubmitInstallFallback}
                onFallbackValueChange={onInstallFallbackValueChange}
            />
        );
    }

    if (method === 'OAuth') {
        return (
            <Stack>
                <Alert variant="light" color="blue" icon={<ShieldCheck size={16} />} title="Sign-in handled by the provider">
                    <Text size="xs">
                        {editing
                            ? `Tokens are managed by ${providerType.displayName}. Re-authenticate below to rotate the refresh token without losing the rest of this connection's configuration.`
                            : `Click below to start the consent flow. You'll be redirected to ${providerType.displayName} to grant access; tokens are stored securely server-side.`}
                    </Text>
                </Alert>
                {editing ? (
                    onReauthenticate && (
                        <Button
                            onClick={onReauthenticate}
                            loading={saving}
                            fullWidth
                            size="md"
                            variant="default"
                            leftSection={<RefreshCw size={16} />}
                            styles={{ root: { height: 'auto', minHeight: 'var(--button-height)' }, label: { whiteSpace: 'normal', paddingBlock: 8 } }}
                        >
                            Re-authenticate with {providerType.displayName}
                        </Button>
                    )
                ) : (
                    <Button
                        onClick={onStartOauth}
                        loading={saving}
                        fullWidth
                        size="md"
                        leftSection={<ShieldCheck size={16} />}
                        styles={{ root: { height: 'auto', minHeight: 'var(--button-height)' }, label: { whiteSpace: 'normal', paddingBlock: 8 } }}
                    >
                        Connect with {providerType.displayName}
                    </Button>
                )}
            </Stack>
        );
    }

    const secretPlaceholder = editing ? '••••••••  (leave blank to keep existing)' : undefined;
    return (
        <Stack>
            {fields.map((f) => {
                if (f.tenantSecret) {
                    return (
                        <ConnectionTenantSecret
                            key={f.name}
                            providerTypeId={providerType.id as string}
                            field={f}
                            value={typeof displayConfig[f.name] === 'string' ? (displayConfig[f.name] as string) : undefined}
                            fetchSecret={onFetchTenantSecret}
                            onValueChange={(v) => onFieldChange(f.name, v)}
                        />
                    );
                }
                return renderField({
                    field: f,
                    value: displayConfig[f.name],
                    onChange: (v) => onFieldChange(f.name, v),
                    secretPlaceholder,
                    onLoadOptions,
                });
            })}
            {launchActions.length > 0 && (
                <>
                    <Divider my="xs" />
                    <Stack gap="xs">
                        {launchActions.map((action) => (
                            <LaunchActionButton
                                key={action.route}
                                action={action}
                                providerTypeId={providerType.id as string}
                                config={config}
                                onCallProviderRoute={onCallProviderRoute}
                                onResolveLaunchUrl={onResolveLaunchUrl}
                            />
                        ))}
                    </Stack>
                </>
            )}
        </Stack>
    );
}

interface LaunchActionButtonProps {
    action: LaunchActionDescriptor;
    providerTypeId: string;
    config: Record<string, unknown>;
    onCallProviderRoute: (providerTypeId: string, route: string, body: Record<string, unknown>) => Promise<unknown>;
    onResolveLaunchUrl?: LaunchUrlResolver;
}

function LaunchActionButton({ action, providerTypeId, config, onCallProviderRoute, onResolveLaunchUrl }: LaunchActionButtonProps): React.ReactElement {
    async function handleClick(): Promise<void> {
        try {
            const clientSide = onResolveLaunchUrl?.(providerTypeId, action.route, config);
            if (typeof clientSide === 'string' && clientSide) {
                window.open(clientSide, '_blank', 'noopener');
                return;
            }
            const result = await onCallProviderRoute(providerTypeId, action.route, config);
            const url = (result as { url?: unknown })?.url;
            if (typeof url === 'string' && url) {
                window.open(url, '_blank', 'noopener');
            }
        } catch {
            // arch-check-ignore — intentional no-op; the caller surfaces errors via notifications
        }
    }

    const Icon = action.icon === 'aws' ? Cloud : ExternalLink;
    return (
        <Stack gap={4}>
            <Button
                variant="default"
                size="md"
                leftSection={<Icon size={16} />}
                onClick={handleClick}
            >
                {action.label}
            </Button>
            {action.description && (
                <Text size="xs" c="dimmed">{action.description}</Text>
            )}
        </Stack>
    );
}

interface InstallFlowAuthProps {
    installFlow: InstallFlowDescriptor;
    saving: boolean;
    fallbackMode: boolean;
    fallbackValue: string;
    onStartInstall: () => void;
    onSwitchToFallback: () => void;
    onBackFromFallback: () => void;
    onSubmitFallback: () => void;
    onFallbackValueChange: (value: string) => void;
}

function InstallFlowAuth({
    installFlow,
    saving,
    fallbackMode,
    fallbackValue,
    onStartInstall,
    onSwitchToFallback,
    onBackFromFallback,
    onSubmitFallback,
    onFallbackValueChange,
}: InstallFlowAuthProps): React.ReactElement {
    if (!fallbackMode) {
        return (
            <Stack>
                <Alert variant="light" color="blue" icon={<ShieldCheck size={16} />} title={installFlow.installTitle}>
                    <Text size="xs">{installFlow.installDescription}</Text>
                </Alert>
                <Button
                    onClick={onStartInstall}
                    loading={saving}
                    fullWidth
                    size="md"
                    leftSection={<ShieldCheck size={16} />}
                    styles={{ root: { height: 'auto', minHeight: 'var(--button-height)' }, label: { whiteSpace: 'normal', paddingBlock: 8 } }}
                >
                    {installFlow.installLabel}
                </Button>
                {installFlow.fallback && (
                    <Text size="xs" c="dimmed" ta="center">
                        <Anchor component="button" type="button" size="xs" onClick={onSwitchToFallback}>
                            {installFlow.fallback.linkLabel}
                        </Anchor>
                    </Text>
                )}
            </Stack>
        );
    }
    if (!installFlow.fallback) return <></>;
    return (
        <Stack>
            <Alert variant="light" color="blue" icon={<ShieldCheck size={16} />} title={installFlow.fallback.title}>
                <Text size="xs">{installFlow.fallback.description}</Text>
            </Alert>
            <TextInput
                label={installFlow.fallback.fieldLabel}
                placeholder={installFlow.fallback.placeholder}
                description={installFlow.fallback.fieldDescription}
                value={fallbackValue}
                onChange={(e) => onFallbackValueChange(e.currentTarget.value)}
                required
            />
            <Group justify="space-between">
                <Anchor component="button" type="button" size="xs" onClick={onBackFromFallback}>
                    ← Back to install
                </Anchor>
                <Button
                    onClick={onSubmitFallback}
                    loading={saving}
                    size="md"
                    leftSection={<ShieldCheck size={16} />}
                >
                    {installFlow.fallback.submitLabel}
                </Button>
            </Group>
        </Stack>
    );
}
